import { Controller } from "@hotwired/stimulus"

// Handles coach and duration selection for training schedule
export default class extends Controller {
  static targets = [
    "coachCard",
    "durationCard",
    "coachInput",
    "durationInput",
    "costCalculator",
    "costDisplay",
    "remainingBudget"
  ]
  
  static values = {
    budget: Number,
    teamSize: Number,
    coachRates: Object,
    selectedCoachId: Number,
    selectedDuration: Number
  }
  
  connect() {
    console.log("Training schedule controller connected")
    
    // Pre-select existing values if present
    this.preselectExistingValues()
    
    // Initial cost calculation if values are pre-selected
    if (this.selectedCoachIdValue && this.selectedDurationValue) {
      this.updateCostCalculator()
    }
  }
  
  selectCoach(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const card = event.currentTarget
    const coachId = parseInt(card.dataset.coachId)
    
    console.log("Selecting coach:", coachId)
    
    // Remove selection from all coach cards
    this.coachCardTargets.forEach(c => {
      const indicator = c.querySelector('.selection-indicator')
      const dot = c.querySelector('.selected-dot')
      const cardBorder = c.querySelector('div > div')
      
      if (indicator) {
        indicator.classList.remove('border-blue-500')
        indicator.classList.add('border-gray-300')
      }
      if (dot) {
        dot.classList.add('hidden')
      }
      if (cardBorder) {
        cardBorder.classList.remove('border-blue-500', 'bg-blue-50')
        cardBorder.classList.add('border-gray-200')
      }
    })
    
    // Add selection to clicked card
    const indicator = card.querySelector('.selection-indicator')
    const dot = card.querySelector('.selected-dot')
    const cardBorder = card.querySelector('div > div')
    
    if (indicator) {
      indicator.classList.remove('border-gray-300')
      indicator.classList.add('border-blue-500')
    }
    if (dot) {
      dot.classList.remove('hidden')
    }
    if (cardBorder) {
      cardBorder.classList.remove('border-gray-200')
      cardBorder.classList.add('border-blue-500', 'bg-blue-50')
    }
    
    // Update selected values
    this.selectedCoachIdValue = coachId
    if (this.hasCoachInputTarget) {
      this.coachInputTarget.value = coachId
    }
    
    this.updateCostCalculator()
  }
  
  selectDuration(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const card = event.currentTarget
    const duration = parseInt(card.dataset.duration)
    
    console.log("Selecting duration:", duration)
    
    // Remove selection from all duration cards
    this.durationCardTargets.forEach(c => {
      const indicator = c.querySelector('.selection-indicator')
      const dot = c.querySelector('.selected-dot')
      const cardBorder = c.querySelector('div > div')
      
      if (indicator) {
        indicator.classList.remove('border-blue-500')
        indicator.classList.add('border-gray-300')
      }
      if (dot) {
        dot.classList.add('hidden')
      }
      if (cardBorder) {
        cardBorder.classList.remove('border-blue-500', 'bg-blue-50')
        cardBorder.classList.add('border-gray-200')
      }
    })
    
    // Add selection to clicked card
    const indicator = card.querySelector('.selection-indicator')
    const dot = card.querySelector('.selected-dot')
    const cardBorder = card.querySelector('div > div')
    
    if (indicator) {
      indicator.classList.remove('border-gray-300')
      indicator.classList.add('border-blue-500')
    }
    if (dot) {
      dot.classList.remove('hidden')
    }
    if (cardBorder) {
      cardBorder.classList.remove('border-gray-200')
      cardBorder.classList.add('border-blue-500', 'bg-blue-50')
    }
    
    // Update selected values
    this.selectedDurationValue = duration
    if (this.hasDurationInputTarget) {
      this.durationInputTarget.value = duration
    }
    
    this.updateCostCalculator()
  }
  
  calculateTeamDiscount(teamSize) {
    if (teamSize <= 2) return 1.0
    if (teamSize <= 5) return 0.9
    if (teamSize <= 10) return 0.8
    if (teamSize <= 15) return 0.7
    return 0.6
  }
  
  updateCostCalculator() {
    if (!this.hasCostCalculatorTarget) return
    
    if (this.selectedCoachIdValue && this.selectedDurationValue && this.coachRatesValue[this.selectedCoachIdValue]) {
      const hourlyRate = this.coachRatesValue[this.selectedCoachIdValue]
      const baseCost = hourlyRate * this.selectedDurationValue * this.teamSizeValue
      const teamDiscount = this.calculateTeamDiscount(this.teamSizeValue)
      const totalCost = Math.round(baseCost * teamDiscount)
      const remaining = this.budgetValue - totalCost
      
      if (this.hasCostDisplayTarget) {
        this.costDisplayTarget.textContent = '$' + totalCost.toLocaleString()
      }
      
      if (this.hasRemainingBudgetTarget) {
        this.remainingBudgetTarget.textContent = 'Remaining: $' + remaining.toLocaleString()
        this.remainingBudgetTarget.className = remaining >= 0 ? 'text-sm text-green-600' : 'text-sm text-red-600'
      }
      
      this.costCalculatorTarget.style.display = 'block'
    } else {
      this.costCalculatorTarget.style.display = 'none'
    }
  }
  
  preselectExistingValues() {
    // Check for pre-selected coach
    const existingCoachId = this.data.get("existingCoachId")
    if (existingCoachId) {
      const coachCard = this.coachCardTargets.find(card => 
        card.dataset.coachId === existingCoachId
      )
      if (coachCard) {
        // Simulate click to select
        this.selectCoach({ 
          currentTarget: coachCard, 
          preventDefault: () => {}, 
          stopPropagation: () => {} 
        })
      }
    }
    
    // Check for pre-selected duration
    const existingDuration = this.data.get("existingDuration")
    if (existingDuration) {
      const durationCard = this.durationCardTargets.find(card => 
        card.dataset.duration === existingDuration
      )
      if (durationCard) {
        // Simulate click to select
        this.selectDuration({ 
          currentTarget: durationCard, 
          preventDefault: () => {}, 
          stopPropagation: () => {} 
        })
      }
    }
  }
}
